import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Panel } from "@/components/kit";
import ResourceScreen from "@/components/ResourceScreen";
import { Truck, Ship, CalendarClock, Boxes, Gauge, RefreshCw, Loader2, PackageCheck, UserCheck, UserX } from "lucide-react";

const titleize = (s) => String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const opts = (values) => values.map((v) => ({ value: v, label: titleize(v) }));
const REF = (kind) => `/admin-dashboard/refs/${kind}`;

const SHIP_STATUS = ["planned", "dispatched", "in_transit", "delivered", "returned", "cancelled"];
const DELIV_STATUS = ["scheduled", "out_for_delivery", "delivered", "failed", "partial"];
const VEHICLE_STATUS = ["available", "in_transit", "maintenance", "out_of_service"];
const VEHICLE_TYPES = ["truck", "van", "trailer", "pickup", "car", "forklift"];

const TONE = {
  emerald: "bg-emerald-500", orange: "bg-orange-500", red: "bg-rose-500",
  blue: "bg-sky-500", amber: "bg-amber-500", purple: "bg-violet-500", slate: "bg-slate-500",
};

const SHIPMENTS = {
  key: "lg-shipments", title: "Shipments", singular: "Shipment", module: "logistics",
  endpoint: "/admin-dashboard/shipments",
  refs: { customers: REF("customers"), warehouses: REF("warehouses") },
  filters: [{ key: "status", label: "Status", options: opts(SHIP_STATUS) }],
  columns: [
    { key: "shipment_number", label: "Shipment #" },
    { key: "customer_id", label: "Customer", ref: "customers", refLabel: "label" },
    { key: "warehouse_id", label: "Origin", ref: "warehouses", refLabel: "label" },
    { key: "tracking_number", label: "Tracking" },
    { key: "dispatch_date", label: "Dispatch" },
    { key: "status", label: "Status" },
  ],
  fields: [
    { key: "shipment_number", label: "Shipment Number", required: true },
    { key: "customer_id", label: "Customer", type: "select", ref: "customers", labelKey: "label" },
    { key: "warehouse_id", label: "Origin Warehouse", type: "select", ref: "warehouses", labelKey: "label", required: true },
    { key: "tracking_number", label: "Tracking Number" },
    { key: "destination_address", label: "Destination Address", type: "textarea" },
    { key: "dispatch_date", label: "Dispatch Date", type: "date" },
    { key: "status", label: "Status", type: "select", options: opts(SHIP_STATUS), default: "planned" },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
};

const DELIVERIES = {
  key: "lg-deliveries", title: "Delivery Schedules", singular: "Delivery", module: "logistics",
  endpoint: "/admin-dashboard/delivery-schedules",
  refs: { shipments: REF("shipments"), customers: REF("customers") },
  filters: [{ key: "status", label: "Status", options: opts(DELIV_STATUS) }],
  columns: [
    { key: "delivery_number", label: "Delivery #" },
    { key: "shipment_id", label: "Shipment", ref: "shipments", refLabel: "label" },
    { key: "scheduled_date", label: "Scheduled" },
    { key: "delivered_date", label: "Delivered" },
    { key: "recipient_name", label: "Recipient" },
    { key: "status", label: "Status" },
  ],
  fields: [
    { key: "delivery_number", label: "Delivery Number", required: true },
    { key: "shipment_id", label: "Shipment", type: "select", ref: "shipments", labelKey: "label" },
    { key: "customer_id", label: "Customer", type: "select", ref: "customers", labelKey: "label" },
    { key: "scheduled_date", label: "Scheduled Date", type: "date" },
    { key: "delivered_date", label: "Delivered Date", type: "date" },
    { key: "recipient_name", label: "Recipient Name" },
    { key: "status", label: "Status", type: "select", options: opts(DELIV_STATUS), default: "scheduled" },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
};

// Delivered goods: read-only view locked to delivered deliveries.
const DELIVERED = {
  key: "lg-delivered", title: "Delivered Goods", singular: "Delivered Delivery", module: "logistics",
  endpoint: "/admin-dashboard/delivery-schedules", readOnly: true, fixedFilter: { status: "delivered" },
  refs: { shipments: REF("shipments"), customers: REF("customers") },
  columns: [
    { key: "delivery_number", label: "Delivery #" },
    { key: "shipment_id", label: "Shipment", ref: "shipments", refLabel: "label" },
    { key: "customer_id", label: "Customer", ref: "customers", refLabel: "label" },
    { key: "delivered_date", label: "Delivered On" },
    { key: "recipient_name", label: "Received By" },
    { key: "status", label: "Status" },
  ],
  fields: [],
};

const VEHICLES = {
  key: "lg-vehicles", title: "Vehicles", singular: "Vehicle", module: "fleet",
  endpoint: "/admin-dashboard/vehicles",
  filters: [
    { key: "status", label: "Condition", options: opts(VEHICLE_STATUS) },
    { key: "vehicle_type", label: "Type", options: opts(VEHICLE_TYPES) },
  ],
  columns: [
    { key: "registration_number", label: "Reg / Plate" },
    { key: "vehicle_type", label: "Type" },
    { key: "status", label: "Condition" },
    { key: "current_location", label: "Current Location" },
    { key: "capacity", label: "Capacity" },
    { key: "assigned_driver", label: "Driver" },
  ],
  fields: [
    { key: "registration_number", label: "Registration / Plate", required: true },
    { key: "vehicle_type", label: "Type", type: "select", options: opts(VEHICLE_TYPES), default: "truck" },
    { key: "make", label: "Make" },
    { key: "model", label: "Model" },
    { key: "capacity", label: "Capacity (kg)", type: "number" },
    { key: "status", label: "Condition / Status", type: "select", options: opts(VEHICLE_STATUS), default: "available" },
    { key: "current_location", label: "Current Location", required: true },
    { key: "assigned_driver", label: "Assigned Driver" },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
};

const INVENTORY = {
  key: "lg-inventory", title: "Inventory Levels", singular: "Inventory Record", module: "logistics",
  endpoint: "/admin-dashboard/inventory-levels", readOnly: true,
  refs: { items: REF("items"), warehouses: REF("warehouses"), bins: REF("bins") },
  columns: [
    { key: "item_id", label: "Item", ref: "items", refLabel: "label" },
    { key: "warehouse_id", label: "Warehouse", ref: "warehouses", refLabel: "label" },
    { key: "bin_id", label: "Bin", ref: "bins", refLabel: "code" },
    { key: "lot_number", label: "Lot" },
    { key: "quantity_on_hand", label: "On Hand" },
    { key: "quantity_reserved", label: "Reserved" },
  ],
  fields: [],
};

// Overview cards; `goto` jumps to a tab when clicked.
const OVERVIEW_CARDS = [
  { key: "total_vehicles", label: "Total Vehicles", tone: "blue", goto: "vehicles" },
  { key: "vehicles_good_condition", label: "In Good Condition", tone: "emerald", goto: "vehicles" },
  { key: "vehicles_under_maintenance", label: "Under Maintenance", tone: "orange", goto: "vehicles" },
  { key: "available_vehicles", label: "Available Now", tone: "emerald", goto: "vehicles" },
  { key: "orders_awaiting_dispatch", label: "Awaiting Dispatch", tone: "amber", goto: "shipments" },
  { key: "deliveries_in_transit", label: "Deliveries In Transit", tone: "purple", goto: "deliveries" },
  { key: "delayed_deliveries", label: "Delayed Deliveries", tone: "red", goto: "deliveries" },
];

const TABS = [
  { key: "overview", title: "Overview", icon: Gauge, overview: true },
  { key: "shipments", title: "Shipments", icon: Ship, config: SHIPMENTS },
  { key: "deliveries", title: "Delivery Schedules", icon: CalendarClock, config: DELIVERIES },
  { key: "delivered", title: "Delivered Goods", icon: PackageCheck, config: DELIVERED },
  { key: "vehicles", title: "Vehicles & Fleet", icon: Truck, config: VEHICLES, fleet: true },
  { key: "inventory", title: "Inventory Levels", icon: Boxes, config: INVENTORY, note: "read-only" },
];

function Kpi({ label, value, tone, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick}
      className={`relative overflow-hidden bg-slate-900/80 border border-slate-800 rounded-md p-4 text-left w-full ${onClick ? "hover:border-orange-600/50 hover:bg-slate-900 transition-colors cursor-pointer" : ""}`}
      data-testid={`lg-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <span className={`absolute top-0 left-0 h-[2px] w-full ${TONE[tone]}`} />
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="font-mono text-3xl font-extrabold text-slate-100 mt-3">{typeof value === "number" ? value.toLocaleString() : (value ?? 0)}</div>
    </Tag>
  );
}

function Overview({ onGoto }) {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/metrics");
      setKpis(data.kpis);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load logistics metrics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="logistics-overview">
      <div className="flex justify-end">
        <button onClick={load} data-testid="lg-refresh" className="flex items-center gap-2 border border-slate-700 hover:border-slate-500 text-slate-300 font-mono text-xs uppercase tracking-wider px-3 py-2 rounded-md transition-colors">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      {loading && !kpis ? (
        <div className="py-16 text-center"><Loader2 className="h-7 w-7 animate-spin text-orange-500 inline" /></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {OVERVIEW_CARDS.map((c) => <Kpi key={c.key} label={c.label} value={kpis?.[c.key]} tone={c.tone} onClick={c.goto ? () => onGoto(c.goto) : undefined} />)}
        </div>
      )}
    </div>
  );
}

// Fleet assignment summary: who has a driver vs who is unassigned.
function FleetAssignment() {
  const [vehicles, setVehicles] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin-dashboard/vehicles?all=true");
      setVehicles(Array.isArray(data) ? data : (data.data || []));
    } catch { setVehicles([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (vehicles === null) return null;
  const assigned = vehicles.filter((v) => (v.assigned_driver || "").trim());
  const unassigned = vehicles.filter((v) => !(v.assigned_driver || "").trim());

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="fleet-assignment">
      <Panel className="p-4" data-testid="fleet-assigned">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-emerald-400"><UserCheck className="h-4 w-4" /><span className="font-mono text-[11px] uppercase tracking-wider">Assigned to a driver</span></div>
          <span className="font-mono text-2xl font-extrabold text-slate-100">{assigned.length}</span>
        </div>
        <div className="mt-3 space-y-1.5 max-h-56 overflow-y-auto">
          {assigned.length === 0 ? <div className="text-xs text-slate-500">No vehicles assigned yet.</div>
            : assigned.map((v) => (
              <div key={v.id} className="flex items-center justify-between text-sm border-b border-slate-800/60 py-1.5" data-testid={`fleet-assigned-${v.registration_number}`}>
                <span className="font-mono text-slate-200">{v.registration_number}</span>
                <span className="text-slate-400 text-xs">{v.assigned_driver}</span>
              </div>
            ))}
        </div>
      </Panel>
      <Panel className="p-4" data-testid="fleet-unassigned">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-amber-400"><UserX className="h-4 w-4" /><span className="font-mono text-[11px] uppercase tracking-wider">Unassigned (no driver)</span></div>
          <span className="font-mono text-2xl font-extrabold text-slate-100">{unassigned.length}</span>
        </div>
        <div className="mt-3 space-y-1.5 max-h-56 overflow-y-auto">
          {unassigned.length === 0 ? <div className="text-xs text-slate-500">Every vehicle has a driver.</div>
            : unassigned.map((v) => (
              <div key={v.id} className="flex items-center justify-between text-sm border-b border-slate-800/60 py-1.5" data-testid={`fleet-unassigned-${v.registration_number}`}>
                <span className="font-mono text-slate-200">{v.registration_number}</span>
                <span className="text-slate-500 text-xs italic">unassigned</span>
              </div>
            ))}
        </div>
      </Panel>
    </div>
  );
}

export default function Logistics() {
  const [active, setActive] = useState(0);
  const tab = TABS[active];
  const gotoTab = (key) => { const i = TABS.findIndex((t) => t.key === key); if (i >= 0) setActive(i); };

  return (
    <div className="space-y-6" data-testid="logistics-module">
      <PageHeader title="Logistics" subtitle="Manage shipments, delivery schedules, delivered goods, fleet vehicles and stock visibility." />

      <div className="flex flex-wrap gap-2 border-b border-slate-800 pb-3" data-testid="logistics-tabs">
        {TABS.map((t, i) => {
          const isActive = i === active;
          return (
            <button
              key={t.key}
              onClick={() => setActive(i)}
              data-testid={`logistics-tab-${t.key}`}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-orange-600/15 text-orange-400 border border-orange-600/30" : "text-slate-400 hover:text-slate-100 border border-transparent"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.title}
              {t.note && <span className="ml-1 text-[10px] font-mono uppercase text-slate-500">{t.note}</span>}
            </button>
          );
        })}
      </div>

      {tab.overview ? (
        <Overview onGoto={gotoTab} />
      ) : (
        <div className="space-y-5">
          {tab.fleet && <FleetAssignment />}
          <ResourceScreen key={tab.config.key} config={tab.config} />
        </div>
      )}

      {tab.key === "inventory" && (
        <Panel className="p-3 text-xs text-slate-500">
          Inventory levels are calculated from the immutable stock ledger and shown here for monitoring. To change stock, use a warehouse Stock Adjustment or Transfer.
        </Panel>
      )}
    </div>
  );
}
